import { z } from 'zod';

/** Phase 13f (06 §26): the enterprise license as the screens see it; 13f-2: the free trial ('trial'). */
export type LicenseStatusName = 'trial' | 'active' | 'reminder' | 'grace' | 'read_only';
export type LicenseProduct = 'companygpt' | 'companycode';
export type LicenseProblemName = 'format' | 'signature' | 'installation' | 'expired';

export interface LicenseInfoDto {
  licenseId: string;
  customer: string;
  products: LicenseProduct[];
  /** YYYY-MM-DD, whole days in Europe/Istanbul */
  validFrom: string;
  validUntil: string;
  kind: 'full' | 'trial';
  issuedAt: string;
}

/** What every signed-in person gets: the rozet, the read-only card, a product that is off. */
export interface LicenseSummaryDto {
  /** false in the bireysel edition: no license, nothing shown */
  enabled: boolean;
  status: LicenseStatusName;
  products: LicenseProduct[];
  /** days left with today: to the end while in the trial, active or reminding, to read-only during the grace */
  daysLeft: number | null;
  /** the last day in force (in the trial: its last day, the day before a license takes over) */
  endsOn: string | null;
  /** the first day of the reminder (the trial's last 7 days), the grace and read-only; null when none comes */
  reminderFrom: string | null;
  graceFrom: string | null;
  readOnlyFrom: string | null;
  /** what the state is about (13f-2): the free trial (in it, or read-only right after it), a license, or neither */
  basis: 'trial' | 'license' | 'none';
  /** the free trial's first and last day, and whether it is over (its end, or a license started); null without one */
  trial: { from: string; until: string; over: boolean } | null;
}

export interface LicenseHistoryDto {
  /** '' for the free trial's row */
  licenseId: string;
  uploadedAt: string;
  /** the person who installed it; null: a tool ("Kurulum aracı") */
  uploadedBy: string | null;
  validFrom: string;
  validUntil: string;
  /** free_trial: the installation's 30-day trial (13f-2), started by the installation itself */
  kind: 'full' | 'trial' | 'free_trial';
  products: LicenseProduct[];
  /** trial: the free trial, running */
  state: 'trial' | 'active' | 'queued' | 'ended' | 'invalid';
}

/** Super admins and the break-glass admin: the Lisans page. */
export interface LicenseDetailDto extends LicenseSummaryDto {
  installationId: string;
  current: LicenseInfoDto | null;
  next: LicenseInfoDto | null;
  history: LicenseHistoryDto[];
  /** Phase 15a (D-143): the installation runs on plain http addresses on purpose (OPPY_ALLOW_PLAIN_HTTP) */
  plainHttp: boolean;
}

/** A file checked before it is applied (LisansYukle). */
export type LicenseCheckDto =
  | { ok: true; license: LicenseInfoDto; installationId: string; startsLater: boolean }
  | { ok: false; problem: LicenseProblemName; license?: LicenseInfoDto };

export const licenseFileBody = z.object({ file: z.string().min(1).max(16_384) });

/** test stacks only (OPPY_LICENSE_TEST_CLOCK): the time the license is judged by; null: the real clock */
export const licenseClockBody = z.object({ at: z.iso.datetime({ offset: true }).nullable() });
